import { isSWSGQLError } from '@/utilities/graphQLClient';

import { isCurrencyISO } from '@/constants/currencies';

import type {
  Transaction,
  TransactionInput,
  ImportedTransactions,
} from '../hooks';

import type { Row } from '../components/Table';
import type { SearchCompaniesResult } from '../hooks/useCompanyBatchSearch';
import { getCurrencyISO } from '../components/CustomCell/Ticker/utils';

import type { CurrencyISO } from '@simplywallst/services/types/endpoints/types/utils/ISO';

const isNil = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

// Not a conclusive check, but the aim is to narrow down the type
export const isTransaction = (value: unknown): value is TransactionInput => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'symbol' in value &&
    'exchangeCode' in value &&
    'transactionDate' in value &&
    'units' in value &&
    'amount' in value &&
    'type' in value &&
    !isNil(value.symbol) &&
    !isNil(value.exchangeCode) &&
    !isNil(value.transactionDate) &&
    !isNil(value.units) &&
    !isNil(value.amount) &&
    !isNil(value.type)
  );
};

export const getTransactionsFromTableData = (rows: Row[]) => {
  const transactions: TransactionInput[] = [];

  for (const row of rows) {
    let date: Date | null = null;

    if (row.date !== null) {
      date = new Date(
        Date.UTC(
          row.date.getFullYear(),
          row.date.getMonth(),
          row.date.getDate()
        )
      );
    }

    const transaction = {
      symbol: row.ticker.symbol,
      exchangeCode: row.ticker.exchangeCode,
      transactionDate: date?.toISOString().split('T')[0],
      units: row.shares,
      amount: row.price,
      type: row.type?.toUpperCase(),
      id: row.transactionId,
    };

    if (isTransaction(transaction)) {
      transactions.push(transaction);
    }
  }

  return transactions;
};

export const getErrorDetails = (error: unknown) => {
  if (!isSWSGQLError(error))
    return {
      type: 'unknown_error',
    };

  // payload too large
  if (error.status === 413)
    return {
      type: 'too_many_transactions',
    };

  return {
    type: error.code,
  };
};

export const getDateWithTimezoneOffset = (value: string): Date => {
  /**
   * transactionDate is returned as an ISO-8601 string UTC e.g. 2023-12-31T00:00:00.000Z
   */
  const date = new Date(value);
  /**
   * Since new Date() converts the date to the local timezone
   *
   * new Date('2023-12-31T00:00:00.000Z')
   *
   * In Los Angeles (UTC-8) the date will be
   * > Sat Dec 30 2023 16:00:00 GMT-0800 (Pacific Standard Time)
   *
   * We need to add the timezone offset back to the date to get the original date.
   *
   * In Los Angeles (UTC-8) the date will be
   * Sun Dec 31 2023 00:00:00 GMT-0800 (Pacific Standard Time)
   *
   * From now on, all dates will be in the local timezone
   */
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const getTableDataFromTransactions = (
  transactions: Transaction[]
): Row[] => {
  return transactions.map((transaction) => {
    return {
      ticker: {
        exchangeCode: transaction.exchangeCode,
        symbol: transaction.symbol,
        companyName: transaction.holding?.providerCompanyName ?? null,
      },
      transactionId: transaction.id,
      date: getDateWithTimezoneOffset(transaction.transactionDate),
      shares: transaction.units,
      price: transaction.amount,
      cost: transaction.amount * transaction.units,
      type: transaction.type.toLowerCase(),
      currencyISO: isCurrencyISO(transaction.currencyISO)
        ? transaction.currencyISO
        : null,
    };
  });
};

export const getTableDataFromImportedTransactions = (
  importedTransactions: ImportedTransactions
): Row[] => {
  if (!importedTransactions) return [];

  return importedTransactions.map((transaction) => {
    const { price, units, transaction_type, trade_date } = transaction;
    const company = transaction.company;
    const currencyISO = company?.data.currencyInfo.reporting_currency_iso;
    return {
      ticker: {
        exchangeCode: company?.exchangeSymbol ?? null,
        symbol: company?.tickerSymbol ?? null,
        companyName: company?.name ?? null,
      },
      transactionId: null,
      date: trade_date ? getDateWithTimezoneOffset(trade_date) : null,
      shares: units,
      price,
      cost: price && units ? price * units : null,
      type: transaction_type ? transaction_type.toLowerCase() : null,
      currencyISO: isCurrencyISO(currencyISO) ? currencyISO : null,
    };
  });
};

/**
 * Populates transaction records with matched company information from Algolia search results
 *
 * @param transactions - Array of imported transactions to be populated with company data.
 * Each transaction contains ticker and other trade details.
 *
 * @param searchResults - Array of company search results from Algolia.
 * Contains company information including listings and symbols.
 *
 * @returns The transactions array with matched company data populated.
 */

export const populateTransactionsWithMatchedCompanies = (
  transactions: ImportedTransactions,
  searchResults: SearchCompaniesResult
): ImportedTransactions => {
  if (!transactions || !searchResults.length) return transactions;

  return transactions.map((transaction) => {
    const matchingCompany = searchResults
      .reduce((acc, curr) => [...acc, ...curr], []) // Flatten the nested arrays
      .find((company) => {
        // Don't match empty tickers
        if (transaction.ticker === '') return false;

        // Check primary listing
        if (company.query === transaction.ticker) return true;
        // Check secondary listings
        return company.listings.find(
          (listing) => listing.uniqueSymbol === transaction.ticker
        );
      });

    if (!matchingCompany) return transaction;

    return {
      ...transaction,
      company: {
        exchangeSymbol: matchingCompany.exchangeSymbol,
        tickerSymbol:
          matchingCompany.uniqueSymbol.split(':')[1] ||
          matchingCompany.uniqueSymbol,
        name: matchingCompany.name,
        data: {
          currencyInfo: {
            reporting_currency_iso: getCurrencyISO(
              matchingCompany.exchangeCountryIso
            ) as CurrencyISO,
          },
        },
      },
    };
  });
};
